import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, FileText, Camera, Globe, Clock } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { SEO } from '@/components/SEO';

const phases = [
  {
    title: 'Pre-Production',
    items: [
      'Location scouting across Mexico',
      'Crew hiring and vetting',
      'Permits and government liaison',
      'Budget planning and scheduling',
      'Equipment sourcing and testing',
      'Talent casting and coordination'
    ]
  },
  {
    title: 'Production',
    items: [
      'Line producing and production management',
      'On-set coordination and logistics',
      'Bilingual production support',
      'Catering and base camp',
      'Transportation and accommodation',
      'Daily reporting and communication'
    ]
  },
  {
    title: 'Wrap & Delivery',
    items: [
      'Media management and backup',
      'Equipment returns and reconciliation',
      'Customs clearance for international gear',
      'Financial wrap and final accounting',
      'Post-production handoff or continuation'
    ]
  }
];

const capabilities = [
  { icon: MapPin, title: 'Location Scouting', desc: 'Access to Mexico\'s most diverse and cinematic locations — from deserts to jungles to modern cities.' },
  { icon: Users, title: 'Experienced Crews', desc: 'Vetted local professionals with international production experience and bilingual communication.' },
  { icon: FileText, title: 'Permits & Logistics', desc: 'Full permit handling, government liaison, and production logistics across all Mexican states.' },
  { icon: Camera, title: 'Equipment Access', desc: 'In-house camera, lighting, grip, and sound packages through our rental division.' },
  { icon: Globe, title: 'International Standards', desc: 'Processes built for international productions — insurance, safety protocols, and communication standards.' },
  { icon: Clock, title: 'End-to-End Execution', desc: 'From the first scout to final delivery — we carry through every phase of production.' },
];

export default function ServicesPage() {
  const [shuffledImages, setShuffledImages] = useState([]);

  // All 44 production images
  const allImages = [
    { src: '/assets/making-of/Baklight in action.jpg', caption: 'Production scale' },
    { src: '/assets/making-of/B07.jpeg', caption: 'On location' },
    { src: '/assets/making-of/B08.jpeg', caption: 'Crew in action' },
    { src: '/assets/making-of/B10.jpeg', caption: 'Camera work' },
    { src: '/assets/making-of/B13.jpeg', caption: 'Equipment ready' },
    { src: '/assets/making-of/B16.jpeg', caption: 'Lighting setup' },
    { src: '/assets/making-of/GOPR4788.JPG', caption: 'Behind the camera' },
    { src: '/assets/making-of/GOPR4821.JPG', caption: 'Field production' },
    { src: '/assets/making-of/IMG_1944.JPG', caption: 'Set design' },
    { src: '/assets/making-of/IMG_2185-3.jpg', caption: 'Location scouting' },
    { src: '/assets/making-of/IMG_2613.jpg', caption: 'Grip & electric' },
    { src: '/assets/making-of/IMG_3798.JPG', caption: 'Director at work' },
    { src: '/assets/making-of/IMG_3970.JPG', caption: 'Production team' },
    { src: '/assets/making-of/IMG_6729.JPG', caption: 'Set preparation' },
    { src: '/assets/making-of/IMG_7056.JPG', caption: 'Cinematic framing' },
    { src: '/assets/making-of/IMG_8262.JPG', caption: 'Camera department' },
    { src: '/assets/making-of/IMG_8947.JPG', caption: 'Sound recording' },
    { src: '/assets/making-of/MO5_6853.JPG', caption: 'DIT station' },
    { src: '/assets/making-of/16110515_228425017604866_42310981258313728_n.jpg', caption: 'Wide shot setup' },
    { src: '/assets/making-of/18252722_1364657966952522_6876024843104419840_n.jpg', caption: 'Lighting work' },
    { src: '/assets/making-of/18299400_1555453054496607_3203283486639128576_n.jpg', caption: 'Mexico sets' },
    { src: '/assets/making-of/19436529_1727493067551262_3167179278584381440_n.jpg', caption: 'Production coordination' },
    { src: '/assets/making-of/19535496_1363626153732613_123335135570427904_n.jpg', caption: 'Art direction' },
    { src: '/assets/making-of/1B0599EC-9B65-4E8A-AA59-1647D8B5D439.JPG', caption: 'Camera movement' },
    { src: '/assets/making-of/229976851_1184461165399005_6329300672036110379_n.jpg', caption: 'Technical crew' },
    { src: '/assets/making-of/230364214_127634629565661_3271456089198906912_n.jpg', caption: 'Set construction' },
    { src: '/assets/making-of/28754081_414852978969444_7841725905780080640_n.jpg', caption: 'Props & wardrobe' },
    { src: '/assets/making-of/50221210_126637751722247_8507135922104460304_n.jpg', caption: 'Production setup' },
    { src: '/assets/making-of/50491169_626035687850849_8491164008274185860_n.jpg', caption: 'Field operations' },
    { src: '/assets/making-of/51396501_439061563500349_1817709554472370142_n.jpg', caption: 'Creative vision' },
    { src: '/assets/making-of/51495318_1996933033943139_4084136748103035404_n.jpg', caption: 'Gaffer work' },
    { src: '/assets/making-of/PHOTO-2025-10-18-18-12-38.jpg', caption: 'Steadicam operation' },
    { src: '/assets/making-of/Patro╠ün Smoky.jpg', caption: 'Commercial shoot' },
    { src: '/assets/making-of/Screenshot 2023-04-25 at 13.18.01.png', caption: 'Monitor village' },
    { src: '/assets/making-of/Screenshot 2023-04-25 at 13.18.09.png', caption: 'Post workflow' },
    { src: '/assets/making-of/WhatsApp Image 2024-06-17 at 14.06.37.jpeg', caption: 'Location shoot' },
    { src: '/assets/making-of/WhatsApp Image 2024-06-17 at 14.07.40 (1).jpeg', caption: 'Crew collaboration' },
    { src: '/assets/making-of/WhatsApp Image 2024-10-25 at 14.45.38.jpeg', caption: 'Set visit' },
    { src: '/assets/making-of/WhatsApp Image 2025-11-22 at 20.15.58.jpeg', caption: 'Final setup' },
    { src: '/assets/making-of/detras-camaras informador.jpg', caption: 'Press coverage' },
    { src: '/assets/making-of/paco red TELETON 03.jpg', caption: 'RED camera work' },
    { src: '/assets/making-of/senkoe3.JPG', caption: 'Documentary work' },
    { src: '/assets/making-of/senkoe5.JPG', caption: 'Interview setup' },
    { src: '/assets/making-of/sp10.png', caption: 'Special projects' },
  ];

  // Shuffle images on component mount
  useEffect(() => {
    const shuffled = [...allImages].sort(() => Math.random() - 0.5);
    setShuffledImages(shuffled);
  }, []); // Only shuffle once on mount

  return (
    <div>
      <SEO 
        title="International Production Services Mexico | Baklight Films"
        description="Full production services for international shoots in Mexico. Location scouting, permits, crew, equipment. Local expertise, global standards."
        path="/services"
      />
      <PageHeader
        meta="Production Services"
        title="Built for international productions."
        subtitle="From prep through delivery, Baklight provides full production services for international shoots across Mexico. Local expertise. Global standards."
      />

      {/* Capabilities Grid */}
      <section className="pb-14 sm:pb-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((item, i) => (
              <SectionReveal key={item.title} delay={i * 0.08}>
                <div className="p-6 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))] h-full">
                  <item.icon size={24} className="text-[hsl(var(--bk-accent))] mb-4" strokeWidth={1.5} />
                  <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Making Of Carousel — Production Scale & Locations */}
      <section className="py-14 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-2">
              Behind the Scenes
            </h2>
            <p className="font-body text-[hsl(var(--bk-fg-muted))] mb-10">Some of our amazing sets</p>
          </SectionReveal>
          <SectionReveal delay={0.1}>
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {shuffledImages.map((img, i) => (
                  <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))]">
                      <img
                        src={img.src}
                        alt={img.caption}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
                        <p className="font-mono-meta text-white text-xs">{img.caption}</p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-2 bg-[hsl(var(--bk-bg-elev))]/90 border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] hover:bg-[hsl(var(--bk-fg))] hover:text-[hsl(var(--bk-bg))]" />
              <CarouselNext className="right-2 bg-[hsl(var(--bk-bg-elev))]/90 border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] hover:bg-[hsl(var(--bk-fg))] hover:text-[hsl(var(--bk-bg))]" />
            </Carousel>
          </SectionReveal>
        </div>
      </section>

      {/* Process Accordion */}
      <section className="py-14 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Our Process</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-10">
              Three phases. One continuity.
            </h2>
          </SectionReveal>
          <SectionReveal delay={0.1}>
            <Accordion type="single" collapsible className="space-y-4">
              {phases.map((phase, i) => (
                <AccordionItem key={phase.title} value={`phase-${i}`} className="border border-[hsl(var(--bk-line))] rounded-lg px-6 bg-[hsl(var(--bk-bg))]">
                  <AccordionTrigger className="font-display text-lg text-[hsl(var(--bk-fg))] hover:no-underline py-5">
                    <span className="flex items-center gap-3">
                      <span className="font-mono-meta text-[hsl(var(--bk-accent))]">{String(i + 1).padStart(2, '0')}</span>
                      {phase.title}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                      {phase.items.map((item) => (
                        <div key={item} className="flex items-start gap-2">
                          <div className="w-1 h-1 rounded-full bg-[hsl(var(--bk-accent))] mt-2 flex-shrink-0" />
                          <span className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">{item}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </SectionReveal>
        </div>
      </section>

      {/* Production Incentive Navigation */}
      <section className="py-14 sm:py-20 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <SectionReveal>
            <div className="p-8 rounded-lg border border-[hsl(var(--bk-accent))]/20 bg-gradient-to-br from-[hsl(var(--bk-accent))]/5 to-transparent">
              <h3 className="font-display text-2xl md:text-3xl text-[hsl(var(--bk-fg))] mb-4">
                Production Incentives in Mexico
              </h3>
              <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mb-6 max-w-[700px] leading-relaxed">
                International productions filming in Mexico can access up to 30% federal tax credit (EFICA) and 40% state cash rebate (Filma Jalisco) — stackable incentives that can cover up to 70% of eligible spend.
              </p>
              <div className="bg-[hsl(var(--bk-bg))] p-5 rounded-lg border border-[hsl(var(--bk-line))] mb-6">
                <p className="font-body text-sm text-[hsl(var(--bk-fg-subtle))] leading-relaxed italic">
                  <strong className="text-[hsl(var(--bk-fg))] not-italic">Important:</strong> Eligibility varies by project type, budget size, and country of origin. We handle the entire application process, compliance tracking, and cost verification on your behalf.
                </p>
              </div>
              <Link
                to="/mexico"
                className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity"
              >
                Learn more about Mexico incentives <ArrowRight size={14} />
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] text-[hsl(var(--bk-fg))]">
              Planning an international shoot in Mexico?
            </h2>
            <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] mt-4 max-w-[500px] mx-auto">
              Let's discuss your project and build a production plan.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 font-body text-sm px-8 py-3 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98] mt-8"
            >
              Request an Estimate <ArrowRight size={16} />
            </Link>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
